/**
 * Reglas de toma de asistencia y asistencia extra-grupo (§12.2–§12.4 del plan).
 * Funciones puras: reciben la instantánea, el actor y el "ahora".
 */
import {
  AsistenciaEntity, AsistenciaRol, BanderasAsistencia, DatosCiclo, EstadoSesion, SesionEntity
} from '../models/asistencia.model';
import { Evaluacion, Hallazgo, hallazgo } from '../models/errores';
import { indice, mentorDeSesion } from './indice';
import { finSesion, inicioSesion, seSolapan, sumarHoras, sumarMinutos } from './tiempo';

export interface VentanaToma {
  abre: Date;
  cierra: Date;
}

export function ventanaToma(s: SesionEntity, b?: Partial<BanderasAsistencia>): VentanaToma {
  const minAntes = Number.isFinite(b?.ventanaAperturaMinAntes) ? Number(b!.ventanaAperturaMinAntes) : 60;
  const horasDespues = Number.isFinite(b?.ventanaCierreHorasDespues) ? Number(b!.ventanaCierreHorasDespues) : 24;
  return {
    abre: sumarMinutos(inicioSesion(s), -minAntes),
    cierra: sumarHoras(finSesion(s), horasDespues)
  };
}

/** ¿La sesión tiene un guardado de lista válido dentro o después de su ventana actual de apertura? */
export function sesionTomadaEnVentanaActual(s: SesionEntity, b?: Partial<BanderasAsistencia>): boolean {
  if (!s.tomadaEn) return false;
  const v = ventanaToma(s, b);
  const tTomada = new Date(s.tomadaEn).getTime();
  return Number.isFinite(tTomada) && tTomada >= v.abre.getTime();
}

/** Estado derivado del reloj: la BD solo persiste transiciones manuales y cierres. */
export function estadoEfectivoSesion(s: SesionEntity, ahora: Date, b: BanderasAsistencia): EstadoSesion {
  if (s.estado === 'CANCELADA') return 'CANCELADA';
  const v = ventanaToma(s, b);
  if (ahora.getTime() < v.abre.getTime()) return 'PROGRAMADA';
  const tomadaValida = sesionTomadaEnVentanaActual(s, b);
  if (s.reabiertaHasta && new Date(s.reabiertaHasta).getTime() > ahora.getTime()) return tomadaValida ? 'TOMADA' : 'ABIERTA';
  if (s.estado === 'CERRADA' && (tomadaValida || (s.cerradaPor && s.cerradaPor !== 'sistema') || ahora.getTime() > v.cierra.getTime())) {
    return 'CERRADA';
  }
  if (ahora.getTime() <= v.cierra.getTime()) return tomadaValida ? 'TOMADA' : 'ABIERTA';
  return 'CERRADA';
}

export interface ActorRegla {
  id: string;
  rol: AsistenciaRol | 'SISTEMA';
}

/** ¿Puede este actor guardar la toma regular de la sesión? Coordinación fuera de ventana exige motivo. */
export function evaluarToma(
  datos: DatosCiclo,
  sesion: SesionEntity,
  actor: ActorRegla,
  ahora: Date,
  motivo?: string
): Evaluacion {
  const b = datos.ciclo.banderas;
  const estado = estadoEfectivoSesion(sesion, ahora, b);
  const bloqueos: Hallazgo[] = [];
  if (estado === 'CANCELADA') bloqueos.push(hallazgo('ASIS-010'));
  else if (estado === 'PROGRAMADA') bloqueos.push(hallazgo('ASIS-014'));
  else if (actor.rol === 'MENTOR_MML') {
    const responsable = mentorDeSesion(datos, sesion)?.id;
    if (responsable !== actor.id) bloqueos.push(hallazgo('ASIS-007'));
    else if (estado === 'CERRADA') bloqueos.push(hallazgo('ASIS-005'));
  } else if (actor.rol === 'COORDINADOR_MML') {
    if (estado === 'CERRADA' && !motivo?.trim()) bloqueos.push(hallazgo('ASIS-009', 'La sesión está cerrada: corregirla exige un motivo.'));
  } else if (actor.rol !== 'SISTEMA') {
    bloqueos.push(hallazgo('SEG-001'));
  }
  return { bloqueos, advertencias: [] };
}

export function esAsistenciaVigente(a: AsistenciaEntity): boolean {
  if (a.validacion === 'ANULADA') return false;
  // Regla de negocio: las personas externas al grupo solo pueden tener registro de asistencia (PRESENTE), nunca de inasistencia o excusa.
  if (a.tipo === 'EXTRA_GRUPO' && a.estado !== 'PRESENTE') return false;
  return true;
}

/**
 * Si el participante fue retirado de la lista de seleccionados por coordinación y vuelto a habilitar después,
 * devuelve el instante (ms) de ese retiro para no arrastrar registros de prueba o ciclos previos descartados.
 */
export function ultimoRetiroDeSeleccionadosMs(datos: DatosCiclo, participanteId: string): number {
  const idx = indice(datos);
  const todas = idx.inscripcionesPorParticipante.get(participanteId) ?? [];
  return todas
    .filter(i => i.estado === 'RETIRADA' && i.finalizadaEn && /quitado del listado/i.test(i.motivoFinalizacion ?? ''))
    .reduce((max, i) => Math.max(max, new Date(i.finalizadaEn!).getTime()), 0);
}

/**
 * Verifica si un registro de asistencia es vigente para el participante e inscripción actual,
 * descartando marcas previas a un retiro de seleccionados o marcas de una fecha antigua de una clase reprogramada al futuro.
 */
export function esAsistenciaVigenteParaParticipante(datos: DatosCiclo, a: AsistenciaEntity, ahora?: Date): boolean {
  if (!esAsistenciaVigente(a)) return false;
  const idx = indice(datos);
  const retiroMs = ultimoRetiroDeSeleccionadosMs(datos, a.participanteId);
  if (retiroMs > 0) {
    const regMs = new Date(a.registradaEn).getTime();
    if (Number.isFinite(regMs) && regMs <= retiroMs) return false;
  }
  const s = idx.sesiones.get(a.sesionId);
  if (s) {
    const v = ventanaToma(s, datos.ciclo.banderas);
    const regMs = new Date(a.registradaEn).getTime();
    if (Number.isFinite(regMs) && regMs < v.abre.getTime() && (!ahora || ahora.getTime() < v.abre.getTime())) {
      return false;
    }
  }
  return true;
}

/**
 * Determina si el participante estaba inscrito en el grupo de la sesión cuando esta finalizó o fue tomada,
 * evitando marcar inasistencias automáticas por clases ocurridas antes de su inscripción en el grupo.
 */
export function participanteEstabaInscritoEnSesion(
  datos: DatosCiclo,
  participanteId: string,
  sesion: SesionEntity
): boolean {
  const idx = indice(datos);
  const finMs = finSesion(sesion).getTime();
  const tomadaValida = sesionTomadaEnVentanaActual(sesion, datos.ciclo.banderas);
  const tomadaMs = tomadaValida && sesion.tomadaEn ? new Date(sesion.tomadaEn).getTime() : NaN;
  const refMs = Number.isFinite(tomadaMs) ? Math.max(finMs, tomadaMs) : finMs;
  const retiroMs = ultimoRetiroDeSeleccionadosMs(datos, participanteId);
  const todas = idx.inscripcionesPorParticipante.get(participanteId) ?? [];
  return todas.some(i => {
    if (i.grupoId !== sesion.grupoId) return false;
    const creadaMs = new Date(i.creadaEn).getTime();
    if (retiroMs > 0 && creadaMs < retiroMs) return false;
    const finalizadaMs = i.finalizadaEn ? new Date(i.finalizadaEn).getTime() : Infinity;
    return creadaMs <= refMs && refMs <= finalizadaMs;
  });
}

/**
 * Determina si dos sesiones (de grupos distintos) corresponden a la misma clase curricular
 * para efectos de equivalencia y conciliación (D1, RF-35, RF-36).
 */
export function sonSesionesEquivalentes(datos: DatosCiclo, sA: SesionEntity, sB: SesionEntity): boolean {
  if (sA.eliminado || sB.eliminado) return false;
  const idx = indice(datos);
  const gA = idx.grupos.get(sA.grupoId);
  const gB = idx.grupos.get(sB.grupoId);
  const mA = idx.modulos.get(sA.moduloId);
  const mB = idx.modulos.get(sB.moduloId);
  const mismoRol = !!gA && !!g2Rol(gA.rolId, gB?.rolId);
  const compartido = Boolean(mA?.compartidoEntreRoles || mB?.compartidoEntreRoles);
  const permitidoEntreRoles = mismoRol || (compartido && datos.ciclo.banderas.equivalenciaModulosCompartidosEntreRoles);
  if (!permitidoEntreRoles) return false;

  if (sA.moduloId && sA.moduloId === sB.moduloId) return true;
  if (mismoRol) {
    if (mA?.orden !== undefined && mB?.orden !== undefined && mA.orden === mB.orden) return true;
    if (sA.numero === sB.numero) return true;
  }
  return false;
}

function g2Rol(rolA?: string, rolB?: string): boolean {
  return !!rolA && !!rolB && rolA === rolB;
}

/**
 * Busca la sesión del grupo titular (`grupoTitularId`) que equivale a `sesionExterna`.
 */
export function sesionEquivalenteEnGrupo(
  datos: DatosCiclo,
  sesionExterna: SesionEntity,
  grupoTitularId: string
): SesionEntity | undefined {
  const sesionesGrupo = (indice(datos).sesionesPorGrupo.get(grupoTitularId) ?? []).filter(s => !s.eliminado);
  return (
    sesionesGrupo.find(s => s.moduloId === sesionExterna.moduloId && sonSesionesEquivalentes(datos, s, sesionExterna)) ??
    sesionesGrupo.find(s => sonSesionesEquivalentes(datos, s, sesionExterna))
  );
}

/** Módulos que cumple un participante (una vez por módulo, ADR-02). */
export function modulosCumplidos(datos: DatosCiclo, participanteId: string, excluirAsistenciaId?: string): Set<string> {
  const idx = indice(datos);
  const insc = idx.inscripcionActiva.get(participanteId);
  const set = new Set<string>();
  for (const a of idx.asistenciasPorParticipante.get(participanteId) ?? []) {
    if (a.id === excluirAsistenciaId) continue;
    if (!esAsistenciaVigenteParaParticipante(datos, a) || a.estado !== 'PRESENTE') continue;
    if (a.cuentaParaModuloId) set.add(a.cuentaParaModuloId);
    const sAsist = idx.sesiones.get(a.sesionId);
    if (sAsist && insc && sAsist.grupoId === insc.grupoId) set.add(sAsist.moduloId);
    if (sAsist && insc && datos.ciclo.banderas.extraGrupoCuentaPorEquivalencia) {
      const eq = sesionEquivalenteEnGrupo(datos, sAsist, insc.grupoId);
      if (eq) set.add(eq.moduloId);
    }
  }
  return set;
}

export interface EvaluacionExtraGrupo extends Evaluacion {
  /** Módulo al que suma por equivalencia (undefined = no suma). */
  cuentaParaModuloId?: string;
  equivalencia: boolean;
  grupoOrigenId?: string;
  inscripcionOrigenId?: string;
  sesionEquivalenteOrigen?: SesionEntity;
  conciliaInasistencia?: boolean;
  sesionChoque?: SesionEntity;
  aforoSuperado: boolean;
  esDelGrupo: boolean;
}

/**
 * Evalúa registrar a un participante en una sesión que NO es la de su grupo (§12.3).
 * Orden: retirado → no inscrito → mismo grupo → ya registrado → choque → advertencias.
 */
export function evaluarExtraGrupo(
  datos: DatosCiclo,
  sesion: SesionEntity,
  participanteId: string,
  ahora: Date
): EvaluacionExtraGrupo {
  const b = datos.ciclo.banderas;
  const idx = indice(datos);
  const res: EvaluacionExtraGrupo = { bloqueos: [], advertencias: [], equivalencia: false, aforoSuperado: false, esDelGrupo: false };

  if (!b.extraGrupoHabilitado) {
    res.bloqueos.push(hallazgo('SEG-001', 'La asistencia extra-grupo está deshabilitada en este ciclo.'));
    return res;
  }
  const activa = idx.inscripcionActiva.get(participanteId);
  if (!activa) {
    const retirada = (idx.inscripcionesPorParticipante.get(participanteId) ?? []).some(i => i.estado === 'RETIRADA');
    res.bloqueos.push(hallazgo(retirada ? 'ASIS-008' : 'ASIS-001'));
    return res;
  }
  res.grupoOrigenId = activa.grupoId;
  res.inscripcionOrigenId = activa.id;
  if (activa.grupoId === sesion.grupoId) {
    res.esDelGrupo = true;
    return res;
  }

  const yaEnEstaSesion = (idx.asistenciasPorSesion.get(sesion.id) ?? []).find(
    a => a.participanteId === participanteId && esAsistenciaVigente(a) && a.estado === 'PRESENTE'
  );
  if (yaEnEstaSesion) {
    res.bloqueos.push(hallazgo('GEN-001', 'Esta persona ya tiene asistencia registrada en esta clase.'));
    return res;
  }

  const choque = (idx.asistenciasPorParticipante.get(participanteId) ?? [])
    .filter(a => esAsistenciaVigente(a) && a.estado === 'PRESENTE' && a.sesionId !== sesion.id)
    .map(a => idx.sesiones.get(a.sesionId))
    .find((s): s is SesionEntity => !!s && seSolapan(s, sesion));
  if (choque) {
    res.sesionChoque = choque;
    res.bloqueos.push(hallazgo('ASIS-004'));
    return res;
  }

  res.advertencias.push(hallazgo('ASIS-002'));

  const grupoSesion = idx.grupos.get(sesion.grupoId);
  const sesionEq = sesionEquivalenteEnGrupo(datos, sesion, activa.grupoId);
  if (b.extraGrupoCuentaPorEquivalencia && sesionEq) {
    res.equivalencia = true;
    res.sesionEquivalenteOrigen = sesionEq;
    res.cuentaParaModuloId = sesionEq.moduloId;
    const marcaOrigen = (idx.asistenciasPorSesion.get(sesionEq.id) ?? []).find(
      a => a.participanteId === participanteId && a.tipo === 'REGULAR' && esAsistenciaVigente(a)
    );
    res.conciliaInasistencia = marcaOrigen?.estado === 'AUSENTE';
    if (modulosCumplidos(datos, participanteId).has(sesionEq.moduloId)) {
      res.advertencias.push(hallazgo('ASIS-003'));
    }
  } else {
    res.advertencias.push(hallazgo('ASIS-011'));
  }

  if (grupoSesion) {
    const presentes = (idx.asistenciasPorSesion.get(sesion.id) ?? []).filter(a => esAsistenciaVigente(a) && a.estado === 'PRESENTE').length;
    const limite = Math.floor(grupoSesion.cupo * (1 + b.margenAforoPct / 100));
    if (presentes + 1 > limite) {
      res.aforoSuperado = true;
      res.advertencias.push(hallazgo('ASIS-012'));
    }
  }
  void ahora;
  return res;
}

/** ¿El módulo/clase de esta sesión ya quedó cubierto por una asistencia PRESENTE en otro grupo? (RF-36) */
export function cubiertoPorEquivalencia(datos: DatosCiclo, sesion: SesionEntity, participanteId: string): AsistenciaEntity | undefined {
  const idx = indice(datos);
  return (idx.asistenciasPorParticipante.get(participanteId) ?? []).find(a => {
    if (!esAsistenciaVigenteParaParticipante(datos, a) || a.estado !== 'PRESENTE' || a.sesionId === sesion.id) return false;
    if (a.grupoSesionId === sesion.grupoId) return false;
    if (a.cuentaParaModuloId === sesion.moduloId) return true;
    const sesionAsistida = idx.sesiones.get(a.sesionId);
    return !!sesionAsistida && datos.ciclo.banderas.extraGrupoCuentaPorEquivalencia && sonSesionesEquivalentes(datos, sesion, sesionAsistida);
  });
}

/**
 * Para una asistencia extra-grupo (PRESENTE), devuelve la sesión equivalente del grupo titular
 * del estudiante y su marca regular en esa sesión (por ejemplo, si tenía AUSENTE y queda conciliada).
 */
export function conciliacionDeExtraGrupo(
  datos: DatosCiclo,
  asistenciaExtra: AsistenciaEntity
): { sesionOrigen?: SesionEntity; marcaOrigen?: AsistenciaEntity; conciliaAusencia: boolean } {
  const idx = indice(datos);
  const sesionExtra = idx.sesiones.get(asistenciaExtra.sesionId);
  const grupoOrigenId = asistenciaExtra.grupoOrigenId || idx.inscripcionActiva.get(asistenciaExtra.participanteId)?.grupoId;
  if (!sesionExtra || !grupoOrigenId || grupoOrigenId === asistenciaExtra.grupoSesionId) {
    return { conciliaAusencia: false };
  }
  const sesionOrigen = sesionEquivalenteEnGrupo(datos, sesionExtra, grupoOrigenId);
  if (!sesionOrigen) return { conciliaAusencia: false };
  const marcaOrigen = (idx.asistenciasPorSesion.get(sesionOrigen.id) ?? []).find(
    a => a.participanteId === asistenciaExtra.participanteId && a.tipo === 'REGULAR' && esAsistenciaVigente(a)
  );
  return {
    sesionOrigen,
    marcaOrigen,
    conciliaAusencia: marcaOrigen?.estado === 'AUSENTE'
  };
}

export type EfectoExtraGrupo = 'SUMA' | 'YA_CUMPLIDO' | 'NO_SUMA';

/**
 * Efecto visible de una asistencia sobre el cumplimiento. El dato `cuentaParaModuloId` se conserva
 * aunque el módulo ya esté cumplido (si la otra marca se anula, esta pasa a sumar; ADR-02), así que
 * la UI y los correos deben distinguir «suma» de «ya cumplido» (ASIS-003).
 */
export function efectoExtraGrupo(datos: DatosCiclo, a: Pick<AsistenciaEntity, 'id' | 'participanteId' | 'sesionId' | 'cuentaParaModuloId'>): EfectoExtraGrupo {
  const idx = indice(datos);
  let modId = a.cuentaParaModuloId;
  if (!modId && a.sesionId && datos.ciclo.banderas.extraGrupoCuentaPorEquivalencia) {
    const sAsist = idx.sesiones.get(a.sesionId);
    const insc = idx.inscripcionActiva.get(a.participanteId);
    if (sAsist && insc) {
      modId = sesionEquivalenteEnGrupo(datos, sAsist, insc.grupoId)?.moduloId;
    }
  }
  if (!modId) return 'NO_SUMA';
  return modulosCumplidos(datos, a.participanteId, a.id).has(modId) ? 'YA_CUMPLIDO' : 'SUMA';
}

export const EFECTO_EXTRA_GRUPO_LABEL: Record<EfectoExtraGrupo, string> = {
  SUMA: 'cuenta por equivalencia',
  YA_CUMPLIDO: 'módulo ya cumplido: no suma de nuevo',
  NO_SUMA: 'no suma'
};
